import * as THREE from 'three';
import { SPHERE_CONFIG } from '../utils/Constants.js';

/**
 * Manages the 360° sphere and raycast sphere
 */
export class SphereManager {
    constructor(scene) {
        this.scene = scene;
        this.sphere = null;
        this.sphereMaterial = null;
        this.raycastSphere = null;
        
        this.initialize();
    }

    /**
     * Initialize the main sphere and raycast sphere
     */
    initialize() {
        this.createMainSphere();
        this.createRaycastSphere();
    }

    /**
     * Create the main 360° sphere
     */
    createMainSphere() {
        const geometry = new THREE.SphereGeometry(
            SPHERE_CONFIG.RADIUS,
            SPHERE_CONFIG.WIDTH_SEGMENTS,
            SPHERE_CONFIG.HEIGHT_SEGMENTS
        );
        
        this.sphereMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            side: THREE.BackSide,
            map: null
        });
        
        this.sphere = new THREE.Mesh(geometry, this.sphereMaterial);

        // Apply same base rotation as transition sphere for consistency
        this.sphere.rotation.y = -(Math.PI / 2);

        this.scene.add(this.sphere);

        console.log('✅ Main sphere created');
    }

    /**
     * Create invisible sphere for raycasting
     */
    createRaycastSphere() {
        const geometry = new THREE.SphereGeometry(
            SPHERE_CONFIG.RADIUS,
            SPHERE_CONFIG.WIDTH_SEGMENTS,
            SPHERE_CONFIG.HEIGHT_SEGMENTS
        );
        
        const material = new THREE.MeshBasicMaterial({
            visible: false,
            side: THREE.FrontSide
        });
        
        this.raycastSphere = new THREE.Mesh(geometry, material);
        this.scene.add(this.raycastSphere);
        
        console.log('✅ Raycast sphere created');
    }

    /**
     * Set the texture on the main sphere
     * @param {THREE.Texture} texture - Texture to apply
     */
    setTexture(texture) {
        console.log('[SphereManager] 🔵 Setting texture on sphere material');
        console.log('   Texture:', texture);
        console.log('   Texture format:', texture.format);
        console.log('   Texture type:', texture.type);
        console.log('   Texture dimensions:', texture.image?.width, 'x', texture.image?.height);
        console.log('   Texture isCompressedTexture:', texture.isCompressedTexture);
        console.log('   Current material:', this.sphereMaterial);
        console.log('   Material color BEFORE:', this.sphereMaterial.color);
        console.log('   Material map BEFORE:', this.sphereMaterial.map);

        // CRITICAL: For compressed textures, we need to recreate the material
        // because some WebGL implementations don't handle material.map changes well
        if (texture.isCompressedTexture) {
            console.log('   🔄 Detected compressed texture - recreating material');

            // Dispose old material
            this.sphereMaterial.dispose();

            // Create new material with the texture
            this.sphereMaterial = new THREE.MeshBasicMaterial({
                map: texture,
                side: THREE.BackSide,
                color: 0xffffff
            });

            // Update sphere mesh
            this.sphere.material = this.sphereMaterial;

            console.log('   ✅ Material recreated with compressed texture');
        } else {
            // Standard texture - just update the map
            this.sphereMaterial.map = texture;
            this.sphereMaterial.needsUpdate = true;
        }

        console.log('   Material color AFTER:', this.sphereMaterial.color);
        console.log('   Material map AFTER:', this.sphereMaterial.map);
        console.log('   Material needsUpdate:', this.sphereMaterial.needsUpdate);
    }

    /**
     * Set rotation of the sphere
     * @param {number} rotationY - Rotation in radians
     */
    setRotation(rotationY) {
        // Apply global offset of -90° (to align with Photo Sphere Viewer)
        this.sphere.rotation.y = rotationY - (Math.PI / 2);
    }

    /**
     * Get the main sphere mesh
     * @returns {THREE.Mesh}
     */
    getSphere() {
        return this.sphere;
    }

    /**
     * Get the raycast sphere mesh
     * @returns {THREE.Mesh}
     */
    getRaycastSphere() {
        return this.raycastSphere;
    }

    /**
     * Get objects for raycasting (both spheres)
     * @returns {THREE.Mesh[]}
     */
    getRaycastObjects() {
        return [this.raycastSphere, this.sphere];
    }

    /**
     * Set brightness of the current texture
     * @param {number} brightness - Brightness value (0-200, 100 is normal)
     */
    setBrightness(brightness) {
        if (this.sphereMaterial && this.sphereMaterial.map) {
            // Convert brightness percentage to multiplier (100% = 1.0)
            const multiplier = brightness / 100;

            // Apply brightness by adjusting material color
            // Color acts as a multiplier on the texture
            const color = new THREE.Color(multiplier, multiplier, multiplier);
            this.sphereMaterial.color = color;

            console.log(`🔆 Brightness set to ${brightness}%`);
        }
    }

    /**
     * Set rotation of the sphere
     * @param {number} rotationRadians - Rotation in radians
     */
    setRotation(rotationRadians) {
        if (this.sphere) {
            // Apply global offset of -90° (to fix forward direction)
            const offsetRadians = -Math.PI / 2;
            this.sphere.rotation.y = rotationRadians + offsetRadians;
        }
    }
}

